import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";
import {
  Chip,
  Grid,
  Pagination,
  useTheme,
  useMediaQuery,
  Box,
  Link,
  Alert,
  Typography,
} from "@mui/material";
import RecipeList from "../Recipes/RecipeList";
import SearchBar from "./SearchBar";
import WebSearchResultsAnimated from "./WebSearchResultsAnimated";
import AnimatedTabSwitcher from "./AnimatedTabSwitcher";
import { useNavigate, useLocation } from "react-router-dom";
import FilterFaceted, { FilterPanel } from "./FilterFaceted";
import supabase from "../supabaseClient";
import SelectedRecipeDetail from "../Recipes/SelectedRecipeDetail";
import RecipeExpandedSkeleton from "../Recipes/Skeletons/RecipeExpandedSkeleton";
import UserContext from "../contexts/userContext";
import { usePreviewPreloader } from "../hooks/usePreviewPreloader";
import { keyframes } from "@mui/material/styles";
import analytics, { AnalyticsEvents } from "../services/analytics";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

/**
 * Track search result click to server for analytics
 * Note: This is a fire-and-forget operation that should not block user actions
 */
const trackClickToServer = async (params, accessToken) => {
  if (!accessToken) return;
  
  try {
    await fetch(`${SERVER_URL}/search/track-click`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      body: JSON.stringify(params),
    });
  } catch (error) {
    // Silently fail - analytics tracking should not block user actions
    console.debug("[ANALYTICS] Click tracking failed:", error.message);
  }
};

// Floating particles animation
const floatUp = keyframes`
  0% { transform: translateY(100vh) rotate(0deg); opacity: 0; }
  10% { opacity: 0.6; }
  90% { opacity: 0.6; }
  100% { transform: translateY(-100vh) rotate(720deg); opacity: 0; }
`;

// Soft glow pulse for background blobs
const glowPulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
`;

// Background with soft accent color blobs - more sophisticated
const AnimatedGradientBackground = ({ isDark }) => {
  const accentPrimary = isDark ? "#BB86FC" : "#6366f1";
  const accentSecondary = isDark ? "#03DAC6" : "#8b5cf6";
  
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: isDark ? "#121212" : "#ffffff",
        zIndex: -2,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      {/* Primary accent blob - top right */}
      <Box
        sx={{
          position: "absolute",
          top: "-20%",
          right: "-10%",
          width: "60vw",
          height: "60vw",
          maxWidth: "800px",
          maxHeight: "800px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentPrimary}15 0%, transparent 70%)`,
          filter: "blur(60px)",
          animation: `${glowPulse} 8s ease-in-out infinite`,
        }}
      />
      
      {/* Secondary accent blob - bottom left */}
      <Box
        sx={{
          position: "absolute",
          bottom: "-30%",
          left: "-15%",
          width: "70vw",
          height: "70vw",
          maxWidth: "900px",
          maxHeight: "900px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentSecondary}12 0%, transparent 70%)`,
          filter: "blur(80px)",
          animation: `${glowPulse} 10s ease-in-out infinite`,
          animationDelay: "-4s",
        }}
      />
      
      {/* Subtle center vignette for focus */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: isDark
            ? "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%)"
            : "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.03) 100%)",
        }}
      />
    </Box>
  );
};

// Floating Particles Component
// Animated counter hook
const useAnimatedCounter = (targetValue, duration = 1000) => {
  const [displayValue, setDisplayValue] = useState(0);
  const previousTarget = useRef(0);
  
  useEffect(() => {
    if (targetValue === 0 || targetValue === previousTarget.current) return;
    
    const startValue = previousTarget.current;
    const startTime = performance.now();
    previousTarget.current = targetValue;
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(startValue + (targetValue - startValue) * easeOut);
      
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(targetValue);
      }
    };
    
    requestAnimationFrame(animate);
  }, [targetValue, duration]);
  
  return displayValue;
};

// UX-focused homepage: Search is the hero, everything else supports it
const StyledHomepage = ({ 
  isDark, 
  recipeCount = 0, 
  cookbookCount = 0, 
  recentSearches = [],
  onSearch,
  onApplyFilters,
  onRandom,
  query,
  SearchBarComponent,
}) => {
  const accentPrimary = isDark ? "#BB86FC" : "#6366f1";
  const accentSecondary = isDark ? "#03DAC6" : "#8b5cf6";
  
  // Animated counters
  const animatedRecipeCount = useAnimatedCounter(recipeCount, 1200);
  const animatedSourceCount = useAnimatedCounter(cookbookCount, 800);
  
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* 1. HERO: Search Bar - The ONE action */}
      <Box sx={{ width: "100%", marginBottom: 3 }}>
        {SearchBarComponent}
      </Box>
      
      {/* 2. CONTEXT: What you're searching - subtle, below search */}
      <Typography
        sx={{
          fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
          fontSize: "0.8rem",
          color: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.3)",
          marginBottom: 4,
        }}
      >
        search{" "}
        <Box 
          component="span" 
          sx={{ 
            color: accentPrimary,
            fontWeight: 500,
          }}
        >
          {animatedRecipeCount.toLocaleString()}
        </Box>
        {" "}recipes across{" "}
        <Box 
          component="span" 
          sx={{ 
            color: accentSecondary,
            fontWeight: 500,
          }}
        >
          {animatedSourceCount}
        </Box>
        {" "}sources
      </Typography>
      
      {/* 3. CONTEXTUAL PROMPT: Time-based or mood-based */}
      {(() => {
        const hour = new Date().getHours();
        let prompt, filterCriteria;
        
        if (hour >= 5 && hour < 10) {
          prompt = "breakfast?";
          filterCriteria = { tags: ["breakfast"] };
        } else if (hour >= 10 && hour < 14) {
          prompt = "lunch ideas?";
          filterCriteria = { tags: ["lunch"] };
        } else if (hour >= 14 && hour < 17) {
          prompt = "afternoon snack?";
          filterCriteria = { tags: ["snack"] };
        } else if (hour >= 17 && hour < 21) {
          prompt = "what's for dinner?";
          filterCriteria = { tags: ["dinner"] };
        } else {
          prompt = "late night snack?";
          filterCriteria = { tags: ["snack"], time: "15" };
        }
        
        return (
          <Typography
            onClick={() => onApplyFilters(filterCriteria)}
            sx={{
              fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
              fontSize: "0.8rem",
              color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                color: accentPrimary,
              },
            }}
          >
            {prompt}
          </Typography>
        );
      })()}
    </Box>
  );
};

const FloatingParticles = ({ isDark, count = 20 }) => {
  const particles = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 15 + 10,
      delay: Math.random() * 10,
      opacity: Math.random() * 0.3 + 0.1,
    }));
  }, [count]);

  const accentPrimary = isDark ? "#BB86FC" : "#6366f1";
  const accentSecondary = isDark ? "#03DAC6" : "#8b5cf6";

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        zIndex: -1,
        pointerEvents: "none",
      }}
    >
      {particles.map((p) => (
        <Box
          key={p.id}
          sx={{
            position: "absolute",
            left: p.left,
            bottom: "-20px",
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            background: p.id % 2 === 0 ? accentPrimary : accentSecondary,
            opacity: p.opacity,
            animation: `${floatUp} ${p.duration}s linear infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </Box>
  );
};

// Filter button placement variants - change via URL param: ?filterPlacement=row|tabs|search|fab
const FILTER_PLACEMENTS = ["row", "tabs", "search", "fab"];
const DEFAULT_FILTER_PLACEMENT = "tabs"; // Filter button in tabs row, opens as drawer

// Badge variants for saved recipes - change via URL param: ?savedBadge=corner|chip|ribbon
const SAVED_BADGE_VARIANTS = ["corner", "chip", "ribbon"];
const DEFAULT_SAVED_BADGE = "chip";

function SearchRecipes() {
  const {user, session} = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  
  // Read filter placement from URL param
  const urlParams = new URLSearchParams(location.search);
  const filterPlacementParam = urlParams.get("filterPlacement");
  const filterPlacement = FILTER_PLACEMENTS.includes(filterPlacementParam) 
    ? filterPlacementParam 
    : DEFAULT_FILTER_PLACEMENT;

  // Read saved badge variant from URL param
  const savedBadgeParam = urlParams.get("savedBadge");
  const savedBadgeVariant = SAVED_BADGE_VARIANTS.includes(savedBadgeParam)
    ? savedBadgeParam
    : DEFAULT_SAVED_BADGE;

  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isNewSearchLoading, setIsNewSearchLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showRecipes, setShowRecipes] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState("recipes"); // 'recipes' or 'web' - only used for tabs variant
  const [webResults, setWebResults] = useState([]);
  const [webLoading, setWebLoading] = useState(false);
  const [webSearchErrorMessage, setWebSearchErrorMessage] = useState(""); // For search errors
  const [webPreviewErrorMessage, setWebPreviewErrorMessage] = useState(""); // For preview errors
  const [webTotalCount, setWebTotalCount] = useState(0);
  const [webPage, setWebPage] = useState(1); // Track current page for "Find More"
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Loading state for Find More button
  const [hasMoreResults, setHasMoreResults] = useState(true); // Whether more results are available
  const [webRecipes, setWebRecipes] = useState({}); // URL -> recipe object for progressive display
  const [failedUrls, setFailedUrls] = useState(new Set()); // Track URLs that failed processing
  const [savedUrls, setSavedUrls] = useState({}); // URL -> recipeId for recipes already in user's catalog
  const [homeStats, setHomeStats] = useState({ recipeCount: 0, cookbookCount: 0 }); // Homepage stats
  const ITEMS_PER_PAGE = 10;
  
  // Fetch homepage stats on mount (for stats variants) - direct Supabase query
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get recipe count
        const { count: recipeCount, error: recipeError } = await supabase
          .from("recipes")
          .select("*", { count: "exact", head: true });
        
        if (recipeError) {
          console.error("Recipe count error:", recipeError);
        }
        
        // Get cookbook count
        const { count: cookbookCount, error: bookError } = await supabase
          .from("books")
          .select("*", { count: "exact", head: true });
        
        if (bookError) {
          console.error("Book count error:", bookError);
        }
        
        console.log("Stats fetched:", { recipeCount, cookbookCount });
        
        setHomeStats({
          recipeCount: recipeCount || 0,
          cookbookCount: cookbookCount || 0,
        });
      } catch (e) {
        console.error("Failed to fetch homepage stats", e);
      }
    };
    
    if (!hasSearched) {
      fetchStats();
    }
  }, [hasSearched]);
  
  // Preview preloader hook for web search results
  const { fetchPreview, cancelAll } = usePreviewPreloader();
  const preloadAbortControllerRef = useRef(null);
  const previewPollIntervalRef = useRef(null);
  const searchResultsRef = useRef([]); // Store search results for polling
  const webRecipesRef = useRef({}); // Track current webRecipes for polling checks
  const currentSearchQueryRef = useRef(""); // Track current search query to prevent duplicate searches
  const isSearchingRef = useRef(false); // Track if search is in progress
  const lastSearchedQueryRef = useRef(""); // Track the last query we successfully searched for (to prevent re-searching same query)

  const navigate = useNavigate();
  const recipeListRef = useRef(null);

  // Keep webRecipesRef in sync with webRecipes state for polling
  useEffect(() => {
    webRecipesRef.current = webRecipes;
  }, [webRecipes]);

  // Cleanup preloading and polling on unmount
  useEffect(() => {
    // Capture ref values for cleanup
    const abortController = preloadAbortControllerRef.current;
    const pollInterval = previewPollIntervalRef.current;
    
    return () => {
      if (abortController) {
        abortController.abort();
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      cancelAll();
    };
  }, [cancelAll]);

  // Check which web results are already saved in user's catalog
  useEffect(() => {
    const checkSavedUrls = async () => {
      if (!user || !session?.access_token || webResults.length === 0) {
        return;
      }

      const urls = webResults.map(r => r.url).filter(Boolean);
      if (urls.length === 0) return;

      try {
        const response = await fetch(`${SERVER_URL}/api/check-saved-urls`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ urls }),
        });

        if (response.ok) {
          const data = await response.json();
          setSavedUrls(prev => ({ ...prev, ...data.savedUrls }));
        }
      } catch (error) {
        console.debug("Failed to check saved URLs:", error);
      }
    };

    checkSavedUrls();
  }, [webResults, user, session]);

  // Extract values directly from URL
  const queryParams = new URLSearchParams(location.search);
  const query = queryParams.get("query") || "";

  // Handle tab switch with analytics tracking
  const handleTabChange = useCallback((tabId) => {
    const previousTab = activeTab;
    setActiveTab(tabId);
    
    // Track tab switch
    analytics.track(AnalyticsEvents.SEARCH_TAB_SWITCHED, {
      from_tab: previousTab,
      to_tab: tabId,
      query: query || '',
    });
  }, [activeTab, query]);

  const books = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("books")
      ? decodeURIComponent(queryParams.get("books")).split(";;")
      : [];
  }, [location.search]);

  const authors = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("authors")
      ? decodeURIComponent(queryParams.get("authors")).split(";;")
      : [];
  }, [location.search]);

  // Extract tags from URL (using tag names)
  const tags = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("tags")
      ? decodeURIComponent(queryParams.get("tags")).split(";;")
      : [];
  }, [location.search]);

  const timeLimit = queryParams.get("time") || ""; // Time limit query parameter
  const ownerId = queryParams.get("owner_id") || ""; // Owner ID filter

  const currentPage = Number(queryParams.get("page") || "1");

  // Build filters object
  const filters = {
    books,
    authors,
    tags,
    time: timeLimit,
    owner: ownerId,
  };

  const selectedFilters = [
    ...books.map((book) => ({ type: "Book", value: book })),
    ...authors.map((author) => ({ type: "Author", value: author })),
    ...tags.map((tagName) => ({ type: "Tag", value: tagName })),
    ...(timeLimit ? [{ type: "Time", value: timeLimit }] : []),
    ...(ownerId ? [{ type: "Owner", value: ownerId }] : []),
  ];

  const updateURL = useCallback(
    (currentQuery, currentFilters, currentPage) => {
      const queryParams = new URLSearchParams();

      // Always set 'query' parameter, even if it's an empty string
      queryParams.set("query", currentQuery);
      
      // Save to recent searches (for homepage recent variant)
      if (currentQuery && currentQuery.trim()) {
        try {
          const stored = localStorage.getItem("recentSearches");
          let recentSearches = stored ? JSON.parse(stored) : [];
          // Remove duplicate if exists
          recentSearches = recentSearches.filter(s => s.toLowerCase() !== currentQuery.trim().toLowerCase());
          // Add to front
          recentSearches.unshift(currentQuery.trim());
          // Keep only last 10
          recentSearches = recentSearches.slice(0, 10);
          localStorage.setItem("recentSearches", JSON.stringify(recentSearches));
        } catch (e) {
          console.error("Failed to save recent search", e);
        }
      }

      if (currentFilters.books.length) {
        queryParams.append("books", currentFilters.books.join(";;"));
      }
      if (currentFilters.authors.length) {
        queryParams.append("authors", currentFilters.authors.join(";;"));
      }
      if (currentFilters.tags.length) {
        queryParams.append("tags", currentFilters.tags.join(";;")); // Include tag names in URL
      }
      if (currentFilters.time) {
        queryParams.append("time", currentFilters.time);
      }
      if (currentFilters.owner) {
        queryParams.append("owner_id", currentFilters.owner);
      }
      if (currentPage > 1) {
        queryParams.append("page", currentPage.toString());
      }

      navigate(`?${queryParams.toString()}`);
    },
    [navigate]
  );

  const parseSearchQuery = (query) => {
    const regex = /([^\s"]+)|"([^"]+)"/g;
    const terms = [];
    let match;

    while ((match = regex.exec(query)) !== null) {
      terms.push(match[2] ? match[2] : match[1]);
    }
    return terms;
  };

  // Initialize prevSearchParamsRef to null
  const prevSearchParamsRef = useRef(null);

  useEffect(() => {
    const searchTerms = parseSearchQuery(query);

    const currentSearchParams = {
      query,
      books,
      authors,
      tags,
      timeLimit,
      ownerId,
    };

    // Determine if it's a new search
    const isNewSearch = () => {
      const prev = prevSearchParamsRef.current;
      if (prev === null) {
        return true; // First render or no previous search parameters
      }
      return (
        prev.query !== currentSearchParams.query ||
        JSON.stringify(prev.books) !== JSON.stringify(currentSearchParams.books) ||
        JSON.stringify(prev.authors) !==
          JSON.stringify(currentSearchParams.authors) ||
        JSON.stringify(prev.tags) !== JSON.stringify(currentSearchParams.tags) ||
        prev.timeLimit !== currentSearchParams.timeLimit ||
        prev.ownerId !== currentSearchParams.ownerId
      );
    };

    const newSearch = isNewSearch(); // Determine if it's a new search before fetching data

    const handleSearch = async () => {
      if (
        !query &&
        books.length === 0 &&
        authors.length === 0 &&
        tags.length === 0 &&
        !timeLimit &&
        !ownerId
      ) {
        return;
      }
      setIsNewSearchLoading(newSearch);
      setLoading(true);
      setHasSearched(true);
      await fetchData(searchTerms, newSearch); // Pass newSearch flag to fetchData
    };

    const fetchData = async (searchTerms, isNewSearchFlag) => {
      const queryParams = new URLSearchParams();

      if (searchTerms.length > 0) {
        queryParams.append("ingredients", searchTerms.join(","));
      }
      // Pass original query for analytics logging (avoids comma-separated tokenized version)
      if (query) {
        queryParams.append("raw_query", query);
      }
      if (books.length > 0) {
        queryParams.append("book_names", books.join(";;"));
      }
      if (authors.length > 0) {
        queryParams.append("book_authors", authors.join(";;"));
      }
      if (tags.length > 0) {
        queryParams.append("tags", tags.join(";;")); // Include tag names in fetch
      }
      queryParams.append("page_limit", ITEMS_PER_PAGE);
      queryParams.append("page_offset", (currentPage - 1) * ITEMS_PER_PAGE);
      if (timeLimit) {
        queryParams.append("time_limit", parseInt(timeLimit));
      }
      if (ownerId) {
        queryParams.append("owner_id", ownerId);
      }

      try {
        // Get the access token
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        // Build headers conditionally
        const headers = {};
        if (accessToken) {
          headers["Authorization"] = `Bearer ${accessToken}`;
        }

        const response = await fetch(
          `${SERVER_URL}/search?${queryParams.toString()}`,
          {
            headers,
          }
        );

        if (!response.ok) {
          throw new Error("Network error while fetching recipes");
        }

        const responseData = await response.json();

        setTotalCount(responseData.total_count);
        setRecipes(responseData.results);
        setLoading(false);
        setShowRecipes(true);
        setHasSearched(true);

        // Track search query on client side (for UX analytics)
        if (query) {
          const filterTypes = [];
          if (books.length > 0) filterTypes.push('book');
          if (tags.length > 0) filterTypes.push('tags');
          if (timeLimit) filterTypes.push('time');
          
          analytics.track(AnalyticsEvents.SEARCH_QUERY, {
            query: query,
            results_count: responseData.total_count,
            has_filters: filterTypes.length > 0,
            filter_types: filterTypes,
          });
        }
        setIsNewSearchLoading(false);

        // Set selectedRecipe only if it's a new search and we have results
        if (isNewSearchFlag && responseData.results.length > 0) {
          setSelectedRecipe(responseData.results[0]);
        }

        // Update previous search parameters after fetching data
        prevSearchParamsRef.current = currentSearchParams;
      } catch (error) {
        console.error("Error fetching recipes:", error);
        setErrorMessage(error.message);
        setLoading(false);
      }
    };

    handleSearch();
  }, [query, books, authors, tags, timeLimit, ownerId, currentPage]);

  const handlePageChange = (event, value) => {
    updateURL(query, filters, value);
  };

  useEffect(() => {
    if (recipeListRef.current) {
      // Scroll the RecipeList container to top on page change
      recipeListRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentPage, recipes]);

  // Scroll to top when switching between tabs (reciply vs web)
  useEffect(() => {
    if (recipeListRef.current) {
      recipeListRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [activeTab]);

  const updateFilters = (typeOrFilters, value, isDelete = false) => {
    // Handle array of filters (from FilterPanel)
    if (Array.isArray(typeOrFilters)) {
      const newFilters = {
        books: [],
        authors: [],
        tags: [],
        time: "",
        owner: filters.owner, // Preserve owner filter
      };
      
      typeOrFilters.forEach(filter => {
        if (filter.type === "Book") {
          newFilters.books.push(filter.value);
        } else if (filter.type === "Author") {
          newFilters.authors.push(filter.value);
        } else if (filter.type === "Tag") {
          newFilters.tags.push(filter.value);
        } else if (filter.type === "Time") {
          newFilters.time = filter.value;
        } else if (filter.type === "Owner") {
          newFilters.owner = filter.value;
        }
      });
      
      updateURL(query, newFilters, 1);
      return;
    }
    
    // Handle individual filter update (from other components)
    const type = typeOrFilters;
    const updatedFilters = { ...filters };

    if (type === "Book") {
      updatedFilters.books = isDelete
        ? filters.books.filter((book) => book !== value)
        : [...filters.books, value];
    } else if (type === "Author") {
      updatedFilters.authors = isDelete
        ? filters.authors.filter((author) => author !== value)
        : [...filters.authors, value];
    } else if (type === "Tag") {
      updatedFilters.tags = isDelete
        ? filters.tags.filter((tagName) => tagName !== value)
        : [...filters.tags, value]; // value is tag name
    } else if (type === "Time") {
      updatedFilters.time = isDelete ? "" : value;
    } else if (type === "Owner") {
      updatedFilters.owner = isDelete ? "" : value;
    }

    updateURL(query, updatedFilters, 1);
  };

  const handleRecipeClick = (recipe, position = null) => {
    setSelectedRecipe(recipe);
    
    // Track recipe viewed (client-side)
    analytics.track(AnalyticsEvents.RECIPE_VIEWED, {
      recipe_id: recipe.id,
      recipe_title: recipe.title,
      source: recipe.books?.name || 'unknown',
      is_web_recipe: !recipe.id || String(recipe.id).startsWith('preview-'),
    });

    // Track search result click (client-side)
    analytics.track(AnalyticsEvents.SEARCH_RESULT_CLICKED, {
      query: query || '',
      result_id: recipe.id,
      result_position: position,
      result_title: recipe.title,
    });

    // Track click to server for analytics aggregation
    trackClickToServer({
      query: query || '',
      result_id: recipe.id,
      result_position: position,
      result_title: recipe.title,
      search_type: 'internal',
    }, session?.access_token);
  };

  // Poll preview status endpoint to get recipes as they're summarized
  // Only show results once recipes are fully summarized
  const pollPreviewStatus = useCallback(async (urlsParam) => {
    // Use provided URLs or fall back to searchResultsRef (for "Find More" polling)
    const urls = urlsParam || searchResultsRef.current?.map(r => r.url).filter(Boolean);
    if (!urls || urls.length === 0) return;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      const headers = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${SERVER_URL}/search/preview-status`, {
        method: "POST",
        headers,
        body: JSON.stringify({ urls }),
      });

      if (!response.ok) {
        return; // Silently fail - polling will retry
      }

      const data = await response.json();
      const status = data.status || {};

      // First pass: collect failed URLs BEFORE updating webRecipes state
      // This avoids calling setFailedUrls inside setWebRecipes callback
      const searchResults = searchResultsRef.current || [];
      const newlyFailedUrls = [];
      for (const result of searchResults) {
        const statusData = status[result.url];
        if (statusData && statusData.error) {
          newlyFailedUrls.push(result.url);
          console.debug(`[Preview Poll] Recipe failed for: ${result.url} - ${statusData.error}`);
        }
      }
      
      // Update failedUrls if there are new failures
      if (newlyFailedUrls.length > 0) {
        setFailedUrls((prev) => {
          const newSet = new Set(prev);
          newlyFailedUrls.forEach(url => newSet.add(url));
          return newSet;
        });
      }

      // Update recipes object with newly available recipes
      // Only update when there are actual changes to prevent unnecessary re-renders
      setWebRecipes((prevRecipes) => {
        const newRecipes = { ...prevRecipes };
        let hasUpdates = false;
        
        for (const result of searchResults) {
          const statusData = status[result.url];
          
          // Skip failed URLs (already handled above)
          if (statusData && statusData.error) {
            continue;
          }
          
          if (statusData && statusData.ready && statusData.recipe) {
            const existingRecipe = newRecipes[result.url];
            const newRecipe = { ...statusData.recipe };
            
            // Always update if it's a new recipe (not in state yet) - this ensures new recipes always appear
            if (!existingRecipe) {
              // New recipe - always add it, even if it seems unchanged
              // This handles cases where recipes finish processing but weren't detected before
              newRecipes[result.url] = Object.assign({}, newRecipe);
              hasUpdates = true;
              console.debug(`[Preview Poll] ✅ New recipe added for: ${result.url}`, {
                hasImage: !!newRecipe.image_url,
                ingredientsCount: newRecipe.ingredients?.length || 0,
                instructionsCount: newRecipe.instructions?.length || 0,
              });
              continue; // Move to next result
            }
            
            // For existing recipes, check for changes
            // Remove cache-busting parameter for comparison (server never sends it)
            // Also decode URL to handle encoding differences (%2C vs ,)
            const normalizeImageUrl = (url) => {
              if (!url) return null;
              try {
                // Remove cache-bust params, then decode URL to normalize encoding
                const cleaned = url.replace(/[?&]_t=\d+/g, '').replace(/\?$/, '');
                // Decode URL to handle encoding differences (e.g., %2C vs ,)
                return decodeURIComponent(cleaned);
              } catch (e) {
                // If decoding fails, just return cleaned URL
                return url.replace(/[?&]_t=\d+/g, '').replace(/\?$/, '');
              }
            };
            
            const existingImageUrl = normalizeImageUrl(existingRecipe?.image_url);
            const newImageUrl = normalizeImageUrl(newRecipe.image_url);
            
            // Detect image changes more reliably:
            // 1. Existing recipe gets an image for the first time (existing is null, new is not)
            // 2. Existing recipe's image changes (both exist but different)
            const imageUrlChanged = (existingImageUrl !== newImageUrl && newImageUrl);
            
            // For existing recipes, only update if there are actual changes
            const recipeChanged = 
              existingRecipe.title !== newRecipe.title ||
              (existingRecipe.ingredients?.length || 0) !== (newRecipe.ingredients?.length || 0) ||
              (existingRecipe.instructions?.length || 0) !== (newRecipe.instructions?.length || 0) ||
              imageUrlChanged;
            
            if (recipeChanged) {
              // Add cache-busting parameter when image URL changes or is new
              if (imageUrlChanged && newRecipe.image_url) {
                try {
                  const imageUrl = newRecipe.image_url;
                  // Remove existing cache-bust parameter if present
                  const urlWithoutCacheBust = imageUrl.replace(/[?&]_t=\d+/g, '').replace(/\?$/, '');
                  
                  if (urlWithoutCacheBust.startsWith('http://') || urlWithoutCacheBust.startsWith('https://')) {
                    const url = new URL(urlWithoutCacheBust);
                    url.searchParams.set('_t', Date.now().toString());
                    newRecipe.image_url = url.toString();
                  } else {
                    const separator = urlWithoutCacheBust.includes('?') ? '&' : '?';
                    newRecipe.image_url = `${urlWithoutCacheBust}${separator}_t=${Date.now()}`;
                  }
                  console.debug(`[Preview Poll] 🔄 Image ${existingRecipe ? 'updated' : 'added'} for: ${result.url}`, {
                    old: existingImageUrl || 'none',
                    new: newImageUrl,
                  });
                } catch (urlError) {
                  // If URL parsing fails, just use the original URL
                  console.warn(`[Preview Poll] Failed to add cache-bust to image URL: ${urlError.message}`);
                }
              } else if (existingRecipe && existingRecipe.image_url && !newRecipe.image_url) {
                // Keep existing image if new one is missing (don't remove it)
                newRecipe.image_url = existingRecipe.image_url;
              }
              
              // Always create a new object reference to ensure React detects the change
              // Use Object.assign to ensure it's a truly new object
              newRecipes[result.url] = Object.assign({}, newRecipe);
              hasUpdates = true;
              
              if (imageUrlChanged) {
                console.debug(`[Preview Poll] 🔄 Recipe image updated for: ${result.url}`, {
                  oldImage: existingImageUrl || 'none',
                  newImage: newImageUrl || 'none',
                  hasImage: !!newRecipe.image_url,
                });
              } else {
                console.debug(`[Preview Poll] Recipe updated (no image change) for: ${result.url}`);
              }
            }
          }
          // Note: Error handling moved to top of loop for better visibility
        }

        // Always return a new object if there are updates
        if (hasUpdates) {
          return newRecipes;
        }
        
        // Even if no updates detected, check if we have any new recipes that weren't in prevRecipes
        // This handles cases where recipes finish processing but the comparison didn't detect changes
        // or cached recipes that appear on first poll
        const newRecipeUrls = Object.keys(newRecipes).filter(url => !prevRecipes[url]);
        if (newRecipeUrls.length > 0) {
          console.debug(`[Preview Poll] ✅ Detected ${newRecipeUrls.length} new recipes (may have been missed by change detection)`);
          return newRecipes;
        }
        
        // No updates and no new recipes - return previous state to prevent unnecessary re-renders
        return prevRecipes;
      });

      // Check if all recipes are ready or failed - if so, we can stop polling early
      // A recipe is "done" if it's ready, has an error, or we've marked it as failed
      // BUT: Also check if recipes are actually in our state to avoid stopping too early
      // AND: Check if images are still being enhanced (ready but no image yet)
      // Note: searchResults already declared above, reuse it here
      const allDone = searchResults.length > 0 && searchResults.every(result => {
        const statusData = status[result.url];
        // Recipe is done if: ready, has error, or we've marked it as failed in state
        const isDone = statusData?.ready || statusData?.error;
        
        // Additional check: if recipe is ready, verify it's actually in our state
        // This prevents stopping polling if recipes finished but weren't added to state yet
        // Use webRecipesRef.current to get the latest state value (avoid stale closure)
        if (statusData?.ready) {
          const inState = webRecipesRef.current[result.url];
          if (!inState) {
            // Recipe is ready but not in state - don't stop polling yet
            console.debug(`[Preview Poll] ⚠️ Recipe ready but not in state yet: ${result.url} - continuing to poll`);
            return false;
          }
          
          // Check if image enhancement is still in progress
          // Server sets `enhancing: true` when recipe is first cached, `enhancing: false` when done
          const recipe = statusData.recipe;
          if (recipe?.enhancing === true) {
            // Image enhancement still running - continue polling
            console.debug(`[Preview Poll] 📷 Enhancement in progress: ${result.url} - continuing to poll`);
            return false;
          }
        }
        
        return isDone;
      });

      if (allDone && previewPollIntervalRef.current) {
        clearInterval(previewPollIntervalRef.current);
        previewPollIntervalRef.current = null;
        const readyCount = searchResults.filter(r => status[r.url]?.ready).length;
        const errorCount = searchResults.filter(r => status[r.url]?.error).length;
        const withImages = searchResults.filter(r => status[r.url]?.recipe?.image_url).length;
        console.debug(`[WEB_SEARCH] ✅ All recipes done (${readyCount} ready, ${withImages} with images, ${errorCount} failed) - stopped polling`);
      }

      // Update results list - only show ready recipes, processing ones at bottom
      // This will be handled by the component based on webRecipes state
      // No need to update webResults here - it's already set from search results
    } catch (error) {
      // Silently handle errors - polling will retry
      console.debug(`[Preview Poll] Error: ${error.message}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies - use refs for stable callback (webRecipes access is intentional for logging only)

  // Web search handler
  const handleWebSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      setWebResults([]);
      setWebTotalCount(0);
      currentSearchQueryRef.current = "";
      isSearchingRef.current = false;
      return;
    }

    // Prevent duplicate searches for the same query
    const normalizedQuery = searchQuery.trim();
    if (currentSearchQueryRef.current === normalizedQuery && isSearchingRef.current) {
      console.debug(`[WEB_SEARCH] Skipping duplicate search for: "${normalizedQuery}"`);
      return;
    }

    // Mark as searching and update current query
    isSearchingRef.current = true;
    currentSearchQueryRef.current = normalizedQuery;

    setWebLoading(true);
    setWebSearchErrorMessage("");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      const headers = {};
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${SERVER_URL}/search/web-search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({
          query: searchQuery,
          limit: ITEMS_PER_PAGE,
          page: webPage,
        }),
      });

      if (!response.ok) {
        throw new Error("Network error while fetching web search results");
      }

      const responseData = await response.json();
      const results = responseData.results || [];
      
      // Track web search query on client side (for UX analytics)
      analytics.track(AnalyticsEvents.WEB_SEARCH_QUERY, {
        query: searchQuery,
        results_count: responseData.total_count || 0,
        page: webPage,
      });
      
      // Store search results in ref for polling
      searchResultsRef.current = results;
      
      // Mark this query as successfully searched
      lastSearchedQueryRef.current = normalizedQuery;
      
      // Reset state for new search
      setWebTotalCount(responseData.total_count || 0);
      setWebRecipes({}); // Reset recipes for new search
      setFailedUrls(new Set()); // Reset failed URLs
      setWebPage(1); // Reset pagination for Find More
      setHasMoreResults(true); // Reset Find More availability
      
      // Replace skeleton results with actual search results
      // Mark them as skeletons until recipes are ready
      const resultsWithSkeletonFlag = results.map(result => ({
        ...result,
        _isSkeleton: true, // Mark as skeleton until recipe is ready
      }));
      setWebResults(resultsWithSkeletonFlag);

      // Clear any existing polling
      if (previewPollIntervalRef.current) {
        clearInterval(previewPollIntervalRef.current);
        previewPollIntervalRef.current = null;
      }

      // Start polling for preview status if we have results
      // Use aggressive initial polling (500ms) then slow down to 1.5s
      if (results.length > 0) {
        const urls = results.map(r => r.url).filter(url => url);
        
        // Track polling phase
        let pollCount = 0;
        const FAST_POLL_COUNT = 10; // First 10 polls at fast rate (5 seconds)
        const FAST_POLL_INTERVAL = 500; // 500ms for first ~5 seconds
        const SLOW_POLL_INTERVAL = 1500; // 1.5s after that
        
        // Start polling immediately
        pollPreviewStatus(urls);
        pollCount++;
        
        // Fast polling for the first few seconds
        const fastPollInterval = setInterval(() => {
          pollPreviewStatus(urls);
          pollCount++;
          
          // Switch to slow polling after FAST_POLL_COUNT
          if (pollCount >= FAST_POLL_COUNT) {
            clearInterval(fastPollInterval);
            console.debug(`[WEB_SEARCH] Switching to slow polling after ${pollCount} fast polls`);
            
            // Start slow polling
            previewPollIntervalRef.current = setInterval(() => {
              pollPreviewStatus(urls);
            }, SLOW_POLL_INTERVAL);
          }
        }, FAST_POLL_INTERVAL);
        
        // Store fast interval ref initially (will be replaced by slow interval)
        previewPollIntervalRef.current = fastPollInterval;

        // Stop polling after 60 seconds (allows time for image enhancements)
        setTimeout(() => {
          if (previewPollIntervalRef.current) {
            clearInterval(previewPollIntervalRef.current);
            previewPollIntervalRef.current = null;
            
            // Mark any URLs that haven't completed as failed
            const currentRecipes = webRecipesRef.current;
            const urlsStillProcessing = urls.filter(url => !currentRecipes[url]);
            if (urlsStillProcessing.length > 0) {
              console.debug(`[WEB_SEARCH] Marking ${urlsStillProcessing.length} URLs as timed out after 60 seconds`);
              setFailedUrls(prev => {
                const newSet = new Set(prev);
                urlsStillProcessing.forEach(url => newSet.add(url));
                return newSet;
              });
            }
            
            console.debug(`[WEB_SEARCH] Stopped polling after 60 seconds`);
          }
        }, 60000);
      }
    } catch (error) {
      console.error("Error fetching web search results:", error);
      setWebSearchErrorMessage(error.message);
      setWebResults([]);
      setWebTotalCount(0);
      currentSearchQueryRef.current = ""; // Reset on error
    } finally {
      setWebLoading(false);
      isSearchingRef.current = false; // Mark search as complete
    }
  }, [webPage, pollPreviewStatus]);

  /**
   * Handle "Find More" button click - fetch additional web results
   */
  const handleFindMore = useCallback(async () => {
    if (!query || isLoadingMore) return;
    
    setIsLoadingMore(true);
    
    try {
      // Collect existing URLs to exclude
      const existingUrls = webResults.map(r => r.url).filter(Boolean);
      
      const response = await fetch(
        `${SERVER_URL}/search/web/more`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            query: query.trim(),
            page: webPage + 1,
            excludeUrls: existingUrls,
          }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch more results");
      }
      
      const data = await response.json();
      
      // Update state
      setWebPage(prev => prev + 1);
      setHasMoreResults(data.hasMore !== false && data.results?.length > 0);
      
      if (data.results && data.results.length > 0) {
        // Append new results
        setWebResults(prev => [...prev, ...data.results]);
        
        // Store these URLs for polling
        searchResultsRef.current = [...searchResultsRef.current, ...data.results];
        
        // Ensure polling is running for new URLs
        const allUrls = searchResultsRef.current.map(r => r.url).filter(Boolean);
        console.debug(`[FIND_MORE] Polling for ${allUrls.length} total URLs (${data.results.length} new)`);
        
        // Poll immediately for new URLs
        pollPreviewStatus(allUrls);
        
        // Start/restart interval if not running
        if (!previewPollIntervalRef.current) {
          previewPollIntervalRef.current = setInterval(() => {
            pollPreviewStatus();
          }, 1500);
        }
        
        console.debug(`[FIND_MORE] Added ${data.results.length} more results (page ${webPage + 1})`);
      } else {
        console.debug(`[FIND_MORE] No more results available`);
      }
    } catch (error) {
      console.error("Error fetching more web results:", error);
      // Don't show error to user for "find more" - just log it
    } finally {
      setIsLoadingMore(false);
    }
  }, [query, webPage, webResults, session?.access_token, isLoadingMore, pollPreviewStatus]);

  // Handle web result click - preview the recipe
  // Use webRecipesRef to avoid recreating this callback on every webRecipes change
  const handleWebResultClick = useCallback(async (url, position = null, domain = null) => {
    // Clear any previous preview errors when starting a new preview
    setWebPreviewErrorMessage("");
    
    // Track web recipe click (client-side)
    analytics.track(AnalyticsEvents.RECIPE_VIEWED, {
      url: url,
      source: 'web_search',
      is_web_recipe: true,
    });

    // Track web search result click (client-side)
    analytics.track(AnalyticsEvents.WEB_SEARCH_RESULT_CLICKED, {
      query: query || '',
      result_url: url,
      result_position: position,
      domain: domain || '',
    });

    // Track click to server for analytics aggregation
    trackClickToServer({
      query: query || '',
      result_url: url,
      result_position: position,
      domain: domain || '',
      search_type: 'web',
    }, session?.access_token);
    
    // If we already have the recipe data from polling, use it directly (no skeleton flash)
    // Use ref to get current value without adding webRecipes to deps
    const existingRecipe = webRecipesRef.current[url];
    if (existingRecipe) {
      setSelectedRecipe(existingRecipe);
      return;
    }
    
    // Only show loading skeleton if we don't have the data yet
    setIsNewSearchLoading(true);
    try {
      const result = await fetchPreview(url);
      if (result.success && result.recipe) {
        setSelectedRecipe(result.recipe);
        setWebPreviewErrorMessage(""); // Clear any previous errors
      } else {
        // Handle different error types - show error in preview area, not in the list
        const errorMsg = result?.error || "Failed to preview recipe from this URL";
        if (errorMsg.includes("No recipes found")) {
          setWebPreviewErrorMessage("This page doesn't appear to contain a recipe.");
        } else {
          setWebPreviewErrorMessage(errorMsg);
        }
        // Don't set selectedRecipe on error - keep showing the list
        setSelectedRecipe(null);
      }
    } catch (error) {
      console.error("Error previewing web recipe:", error);
      setWebPreviewErrorMessage(error.message || "Failed to preview recipe");
      setSelectedRecipe(null);
    } finally {
      setIsNewSearchLoading(false);
    }
  }, [fetchPreview, query, session]);

  // Sync selectedRecipe with webRecipes when image or other data updates
  // This ensures the preview panel updates when polling brings in new images
  // ONLY sync if this is a preview recipe (not a database recipe)
  useEffect(() => {
    if (!selectedRecipe?.path) return;
    
    // Don't sync database recipes - they have their own data
    // Only preview recipes have IDs starting with "preview-"
    const isPreviewRecipe = selectedRecipe?.id && typeof selectedRecipe.id === 'string' && selectedRecipe.id.startsWith("preview-");
    if (!isPreviewRecipe) return;
    
    const updatedRecipe = webRecipes[selectedRecipe.path];
    if (!updatedRecipe) return;
    
    // Check if the webRecipes version has updates we should sync
    const currentImageUrl = selectedRecipe.image_url?.replace(/[?&]_t=\d+/g, '').replace(/\?$/, '') || '';
    const newImageUrl = updatedRecipe.image_url?.replace(/[?&]_t=\d+/g, '').replace(/\?$/, '') || '';
    
    // Sync if: image changed, or any other fields we care about changed
    const imageChanged = newImageUrl && currentImageUrl !== newImageUrl;
    const ingredientsChanged = (updatedRecipe.ingredients?.length || 0) !== (selectedRecipe.ingredients?.length || 0);
    const instructionsChanged = (updatedRecipe.instructions?.length || 0) !== (selectedRecipe.instructions?.length || 0);
    const timeChanged = (updatedRecipe.time || 0) !== (selectedRecipe.time || 0);
    const inactiveTimeChanged = (updatedRecipe.inactive_time || 0) !== (selectedRecipe.inactive_time || 0);
    
    if (imageChanged || ingredientsChanged || instructionsChanged || timeChanged || inactiveTimeChanged) {
      console.debug(`[Preview Sync] Updating selectedRecipe from webRecipes`, {
        url: selectedRecipe.path,
        imageChanged,
        ingredientsChanged,
        instructionsChanged,
        timeChanged,
        inactiveTimeChanged,
        oldImage: currentImageUrl || 'none',
        newImage: newImageUrl || 'none',
      });
      
      // Merge the updated recipe with current selectedRecipe (preserving any local state)
      setSelectedRecipe(prev => ({
        ...prev,
        ...updatedRecipe,
        // Add cache-bust to force image reload if image changed
        image_url: imageChanged && updatedRecipe.image_url 
          ? `${updatedRecipe.image_url}${updatedRecipe.image_url.includes('?') ? '&' : '?'}_t=${Date.now()}`
          : updatedRecipe.image_url || prev?.image_url,
      }));
    }
  }, [webRecipes, selectedRecipe?.id, selectedRecipe?.path, selectedRecipe?.image_url, selectedRecipe?.ingredients?.length, selectedRecipe?.instructions?.length, selectedRecipe?.time, selectedRecipe?.inactive_time]);

  // Trigger web search immediately when a search query is executed (regardless of active tab)
  // This is the PRIMARY trigger - runs once when query/hasSearched changes
  // NOTE: handleWebSearch is NOT in deps to prevent re-triggers when it's recreated
  useEffect(() => {
    const normalizedQuery = query?.trim() || "";
    if (normalizedQuery.length > 0 && hasSearched) {
      // Only trigger if:
      // 1. This is a DIFFERENT query than what we last successfully searched for
      // 2. AND we're not currently searching
      const isNewQuery = lastSearchedQueryRef.current !== normalizedQuery;
      const notCurrentlySearching = !isSearchingRef.current;
      
      if (isNewQuery && notCurrentlySearching) {
        console.debug(`[WEB_SEARCH] Triggering search for new query: "${normalizedQuery}" (last: "${lastSearchedQueryRef.current}")`);
        
        // Show skeleton loaders immediately while search is happening
        setWebLoading(true);
        setWebRecipes({}); // Clear previous recipes
        setFailedUrls(new Set()); // Clear failed URLs
        
        // Show skeleton loaders immediately (before search results come back)
        // Create placeholder skeleton results
        const skeletonResults = Array.from({ length: ITEMS_PER_PAGE }, (_, index) => ({
          url: `skeleton-${index}-${Date.now()}`,
          title: "",
          snippet: "",
          domain: "",
          _isSkeleton: true, // Mark as skeleton
        }));
        setWebResults(skeletonResults);
        
        // Always trigger web search when a query is executed, not just when web tab is active
        // This allows server-side preloading to start immediately
        handleWebSearch(normalizedQuery);
      } else {
        console.debug(`[WEB_SEARCH] Skipping duplicate search - query: "${normalizedQuery}", last: "${lastSearchedQueryRef.current}", searching: ${isSearchingRef.current}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, hasSearched]); // Intentionally exclude handleWebSearch to prevent re-triggers

  // Theme-aware colors
  const isDark = theme.palette.mode === "dark";

  // Calculate ready web recipes count (only summarized recipes that are actually displayed)
  const readyWebRecipesCount = useMemo(() => {
    return webResults.filter(
      (result) => webRecipes[result.url] && !failedUrls.has(result.url)
    ).length;
  }, [webResults, webRecipes, failedUrls]);

  return (
    <Grid
      container
      sx={{
        height: "calc(100vh - 90px)",
        display: !hasSearched ? "flex" : "",
        justifyContent: !hasSearched ? "center" : "",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated gradient background - only before search */}
      {!hasSearched && <AnimatedGradientBackground isDark={isDark} />}
      
      {/* Floating particles - only before search */}
      {!hasSearched && <FloatingParticles isDark={isDark} count={15} />}
      
      {/* Before Search */}
      {!hasSearched && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: isMobile ? "calc(100% - 2em)" : "80vw",
              maxWidth: "800px",
            }}
          >
            <StyledHomepage
              isDark={isDark}
              recipeCount={homeStats.recipeCount}
              cookbookCount={homeStats.cookbookCount}
              recentSearches={(() => {
                try {
                  const stored = localStorage.getItem("recentSearches");
                  return stored ? JSON.parse(stored).slice(0, 5) : [];
                } catch (e) {
                  return [];
                }
              })()}
              onSearch={(q) => updateURL(q, filters, 1)}
              onApplyFilters={(newFilters) => updateURL("", { ...filters, ...newFilters }, 1)}
              onRandom={() => {
                const randomTerms = ["chicken", "pasta", "salad", "soup", "cake", "bread", "rice", "beef"];
                const randomTerm = randomTerms[Math.floor(Math.random() * randomTerms.length)];
                updateURL(randomTerm, filters, 1);
              }}
              query={query}
              SearchBarComponent={
                <SearchBar
                  query={query}
                  setQuery={(newQuery) => updateURL(newQuery, filters, 1)}
                  handleSearch={() => updateURL(query, filters, 1)}
                  isSearching={loading || webLoading}
                />
              }
            />
            
            {/* Selected filters - shown below homepage content if any */}
            {selectedFilters.length > 0 && (
              <Grid
                container
                item
                xs={12}
                spacing={1}
                sx={{ marginTop: theme.spacing(2), justifyContent: "center" }}
              >
                {selectedFilters.map((filter, index) => {
                  const isDark = theme.palette.mode === "dark";
                  const accentPrimary = isDark ? "#BB86FC" : "#6366f1";
                  return (
                    <Chip
                      key={index}
                      label={
                        <span>
                          {filter.type.toLowerCase()}:{" "}
                          <i>
                            {filter.type === "Time"
                              ? `< ${filter.value} minutes`
                              : filter.type === "Owner" &&
                                user &&
                                filter.value === user.id
                              ? user.email
                              : filter.value}
                          </i>
                        </span>
                      }
                      onDelete={() => {
                        if (filter.type === "Time") {
                          updateFilters(filter.type, "", true);
                        } else {
                          updateFilters(filter.type, filter.value, true);
                        }
                      }}
                      sx={{
                        borderRadius: "10px",
                        fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                        fontSize: "11px",
                        letterSpacing: "-0.01em",
                        margin: theme.spacing(0, 0.5, 0.5, 0),
                        background: isDark
                          ? `linear-gradient(135deg, ${accentPrimary}15, ${accentPrimary}08)`
                          : `linear-gradient(135deg, ${accentPrimary}12, ${accentPrimary}06)`,
                        border: `1px solid ${accentPrimary}30`,
                        color: theme.palette.text.primary,
                        transition: "all 0.2s ease",
                        "& .MuiChip-deleteIcon": {
                          color: accentPrimary,
                          opacity: 0.7,
                          "&:hover": {
                            color: accentPrimary,
                            opacity: 1,
                          },
                        },
                        "&:hover": {
                          background: isDark
                            ? `linear-gradient(135deg, ${accentPrimary}20, ${accentPrimary}12)`
                            : `linear-gradient(135deg, ${accentPrimary}18, ${accentPrimary}10)`,
                        },
                      }}
                    />
                  );
                })}
              </Grid>
            )}
          </Box>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              right: 0,
              padding: "1em",
            }}
          >
            <Link
              href="/privacy-policy"
              underline="none"
              color="textSecondary"
              sx={{
                fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                fontSize: "11px",
                letterSpacing: "-0.01em",
                opacity: 0.6,
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                transition: "opacity 0.2s ease",
                "&:hover": {
                  opacity: 1,
                },
              }}
            >
              privacy policy
            </Link>
          </Box>
        </>
      )}

      {/* After Search */}
      {hasSearched && (
        <Grid container spacing={2} style={{ height: "100%" }}>
          {/* Left Column */}
          <Grid
            item
            xs={12}
            md={5}
            lg={4}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Desktop: Fixed header with separate scrollable content */}
            {/* Mobile: Single scrollable container - everything scrolls together */}
            
            {/* Header section - fixed on desktop, scrolls on mobile */}
            {!isMobile && (
              <div>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <SearchBar
                      query={query}
                      setQuery={(newQuery) => updateURL(newQuery, filters, 1)}
                      handleSearch={() => updateURL(query, filters, 1)}
                      isSearching={loading || webLoading}
                    />
                  </Box>
                  {filterPlacement === "search" && (
                    <FilterFaceted
                      selectedFilters={selectedFilters}
                      updateFilters={updateFilters}
                      query={query}
                      compact
                    />
                  )}
                </Box>
                {hasSearched && (
                  <Box sx={{ marginTop: theme.spacing(1.5), mb: 1, display: "flex", alignItems: "center" }}>
                    <AnimatedTabSwitcher
                      activeTab={activeTab}
                      onTabChange={handleTabChange}
                      yourRecipesCount={totalCount}
                      webResultsCount={readyWebRecipesCount}
                      webResultsLoading={webLoading || (webResults.length > 0 && readyWebRecipesCount === 0)}
                    />
                    {filterPlacement === "tabs" && activeTab === "recipes" && (
                      <Box sx={{ ml: "auto", pl: 1 }}>
                        <FilterFaceted
                          selectedFilters={selectedFilters}
                          updateFilters={updateFilters}
                          query={query}
                          compact
                          isOpen={showFilters}
                          onToggle={() => setShowFilters(!showFilters)}
                        />
                      </Box>
                    )}
                  </Box>
                )}
                {filterPlacement === "tabs" && activeTab === "recipes" && (
                  <FilterPanel
                    isOpen={showFilters}
                    selectedFilters={selectedFilters}
                    updateFilters={(newFilters) => {
                      const newFiltersObj = {
                        books: [],
                        authors: [],
                        tags: [],
                        time: "",
                        owner: filters.owner,
                      };
                      newFilters.forEach(f => {
                        if (f.type === "Book") newFiltersObj.books.push(f.value);
                        else if (f.type === "Author") newFiltersObj.authors.push(f.value);
                        else if (f.type === "Tag") newFiltersObj.tags.push(f.value);
                        else if (f.type === "Time") newFiltersObj.time = f.value;
                      });
                      updateURL(query, newFiltersObj, 1);
                      setShowFilters(false);
                    }}
                    query={query}
                    onClose={() => setShowFilters(false)}
                  />
                )}
                {activeTab === "recipes" && filterPlacement === "row" && (
                  <Box sx={{ mt: 0.5, mb: 0.5 }}>
                    <FilterFaceted
                      selectedFilters={selectedFilters}
                      updateFilters={updateFilters}
                      query={query}
                    />
                  </Box>
                )}
              </div>
            )}

            {/* Scrollable content area */}
            <Box
              ref={recipeListRef}
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
                paddingTop: "4px",
                paddingBottom: isMobile ? "60px" : 0, // Space for fixed pagination on mobile
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Mobile only: Search bar and tabs inside scrollable area */}
              {isMobile && (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <SearchBar
                        query={query}
                        setQuery={(newQuery) => updateURL(newQuery, filters, 1)}
                        handleSearch={() => updateURL(query, filters, 1)}
                        isSearching={loading || webLoading}
                      />
                    </Box>
                    {filterPlacement === "search" && (
                      <FilterFaceted
                        selectedFilters={selectedFilters}
                        updateFilters={updateFilters}
                        query={query}
                        compact
                      />
                    )}
                  </Box>
                  {hasSearched && (
                    <Box sx={{ marginTop: theme.spacing(1.5), mb: 1, display: "flex", alignItems: "center" }}>
                      <AnimatedTabSwitcher
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                        yourRecipesCount={totalCount}
                        webResultsCount={readyWebRecipesCount}
                        webResultsLoading={webLoading || (webResults.length > 0 && readyWebRecipesCount === 0)}
                      />
                      {filterPlacement === "tabs" && activeTab === "recipes" && (
                        <Box sx={{ ml: "auto", pl: 1 }}>
                          <FilterFaceted
                            selectedFilters={selectedFilters}
                            updateFilters={updateFilters}
                            query={query}
                            compact
                            isOpen={showFilters}
                            onToggle={() => setShowFilters(!showFilters)}
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                  {filterPlacement === "tabs" && activeTab === "recipes" && (
                    <Box sx={{ position: "relative", zIndex: 10 }}>
                      <FilterPanel
                        isOpen={showFilters}
                        selectedFilters={selectedFilters}
                        updateFilters={(newFilters) => {
                          const newFiltersObj = {
                            books: [],
                            authors: [],
                            tags: [],
                            time: "",
                            owner: filters.owner,
                          };
                          newFilters.forEach(f => {
                            if (f.type === "Book") newFiltersObj.books.push(f.value);
                            else if (f.type === "Author") newFiltersObj.authors.push(f.value);
                            else if (f.type === "Tag") newFiltersObj.tags.push(f.value);
                            else if (f.type === "Time") newFiltersObj.time = f.value;
                          });
                          updateURL(query, newFiltersObj, 1);
                          setShowFilters(false);
                        }}
                        query={query}
                        onClose={() => setShowFilters(false)}
                      />
                    </Box>
                  )}
                  {activeTab === "recipes" && filterPlacement === "row" && (
                    <Box sx={{ mt: 0.5, mb: 0.5 }}>
                      <FilterFaceted
                        selectedFilters={selectedFilters}
                        updateFilters={updateFilters}
                        query={query}
                      />
                    </Box>
                  )}
                </>
              )}

              {/* Recipe/Web content */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
              {/* Show either RecipeList or WebSearchResults based on active tab */}
              <Box
                key={activeTab}
                sx={{
                  animation: "fadeInContent 0.25s ease-out",
                  "@keyframes fadeInContent": {
                    "0%": { opacity: 0, transform: "translateY(8px)" },
                    "100%": { opacity: 1, transform: "translateY(0)" },
                  },
              }}
            >
              {activeTab === "recipes" ? (
                <RecipeList
                  loading={loading}
                  errorMessage={errorMessage}
                  recipes={showRecipes ? recipes : []}
                  hasSearched={hasSearched}
                  showResultsText={false}
                  totalCount={totalCount}
                  onRecipeClick={handleRecipeClick}
                  disableExpansion={!isMobile}
                  selectedRecipe={!isMobile && selectedRecipe}
                />
              ) : (
                  <WebSearchResultsAnimated
                  results={webResults}
                  loading={webLoading}
                  errorMessage={webSearchErrorMessage}
                  onResultClick={handleWebResultClick}
                  totalCount={webTotalCount}
                  recipes={webRecipes}
                  failedUrls={failedUrls}
                    savedUrls={savedUrls}
                    savedBadgeVariant={savedBadgeVariant}
                    onRecipeSaved={(url, recipeId) => setSavedUrls(prev => ({ ...prev, [url]: recipeId }))}
                    onFindMore={handleFindMore}
                    isLoadingMore={isLoadingMore}
                    hasMoreResults={hasMoreResults}
                    targetResultCount={ITEMS_PER_PAGE}
                />
              )}
              </Box>
              {/* Pagination - Floating glass pill */}
              {activeTab === "recipes" && recipes.length > 0 && (() => {
                const pageCount = Math.ceil(totalCount / ITEMS_PER_PAGE);
                const isDark = theme.palette.mode === "dark";
                const accent = isDark ? "#BB86FC" : "#6366f1";
                
                return (
                  <Box
                    sx={{
                      position: isMobile ? "fixed" : "sticky",
                      bottom: isMobile ? 20 : 0,
                      left: isMobile ? "50%" : "auto",
                      transform: isMobile ? "translateX(-50%)" : "none",
                      display: "flex",
                      justifyContent: "center",
                      py: isMobile ? 0 : 1.5,
                      zIndex: 100,
                      marginTop: isMobile ? 0 : "auto", // Push to bottom on desktop when content is short
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        padding: isMobile ? "8px 12px" : "8px 14px",
                        borderRadius: "16px",
                        backgroundColor: isDark
                          ? "rgba(30, 30, 34, 0.95)"
                          : "rgba(255, 255, 255, 0.95)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: isDark
                          ? "1px solid rgba(255, 255, 255, 0.12)"
                          : "1px solid rgba(0, 0, 0, 0.08)",
                        boxShadow: isDark
                          ? "0 8px 32px rgba(0, 0, 0, 0.6)"
                          : "0 8px 32px rgba(0, 0, 0, 0.15)",
                      }}
                    >
                      <Pagination
                        count={pageCount}
                        page={currentPage}
                        onChange={handlePageChange}
                        size="small"
                        siblingCount={1}
                        boundaryCount={1}
                        sx={{
                          fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                          "& .MuiPagination-ul": {
                            flexWrap: "nowrap",
                          },
                          "& .MuiPaginationItem-root": {
                            fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                            fontSize: "0.8rem",
                            borderRadius: "6px",
                            minWidth: isMobile ? "30px" : "28px",
                            height: isMobile ? "30px" : "28px",
                            margin: "0 2px",
                            padding: 0,
                            transition: "all 0.15s ease",
                          },
                          "& .MuiPaginationItem-root.Mui-selected": {
                            backgroundColor: isDark
                              ? "rgba(187, 134, 252, 0.25)"
                              : "rgba(99, 102, 241, 0.18)",
                            color: accent,
                            fontWeight: 600,
                          },
                          "& .MuiPaginationItem-previousNext": {
                            minWidth: isMobile ? "30px" : "28px",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                );
              })()}
              {/* Web results don't need pagination - they load progressively with the "Loading X more..." indicator */}
              </Box>
            </Box>
          </Grid>

          {/* Right Column */}
          {!isMobile && (
            <Grid
              item
              xs={12}
              md={7}
              lg={8}
              style={{ height: "100%", overflowY: "hidden" }}
            >
              {isNewSearchLoading ? (
                <RecipeExpandedSkeleton />
              ) : selectedRecipe ? (
                <SelectedRecipeDetail 
                  recipe={selectedRecipe} 
                  onRecipeUpdate={(updatedRecipe) => {
                    setSelectedRecipe(updatedRecipe);
                  }}
                  onRecipeSaved={(url, recipeId) => {
                    setSavedUrls(prev => ({ ...prev, [url]: recipeId }));
                  }}
                  savedRecipeId={selectedRecipe?.path ? savedUrls[selectedRecipe.path] : null}
                />
              ) : webPreviewErrorMessage ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    padding: theme.spacing(2),
                  }}
                >
                  <Alert severity="warning" sx={{ maxWidth: "500px" }}>
                    {webPreviewErrorMessage}
                  </Alert>
                </Box>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                    fontSize: "0.85rem",
                    color: theme.palette.text.secondary,
                    opacity: 0.7,
                    letterSpacing: "-0.01em",
                  }}
                >
                  no preview available
                </div>
              )}
            </Grid>
          )}
        </Grid>
      )}
      
      {/* FAB Filter placement - floating button */}
      {filterPlacement === "fab" && (
        <FilterFaceted
          selectedFilters={selectedFilters}
          updateFilters={updateFilters}
          query={query}
          placement="fab"
        />
      )}
    </Grid>
  );
}

export default SearchRecipes;